import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { ArrowLeft, Copy, Eye, HardDrive, RotateCcw, Undo2, Wifi, WifiOff } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { applyAction, expire, newDraft, undo } from "@/lib/draft";
import { findDraft, load, save, useChampions, useRoles } from "@/lib/drafterData";
import DraftBoard, { SECONDARY_BTN, SIDE_STYLE } from "@/components/drafter/DraftBoard";

const POLL_MS = 1500;

function Shell({ children }) {
  return (
    <div data-testid="draft-room" className="page-enter mx-auto max-w-7xl px-4 py-10 sm:px-8 lg:py-16">
      <Link to="/drafter" className="inline-flex items-center gap-1.5 text-sm text-slate-400 transition-colors hover:text-orange-400">
        <ArrowLeft className="h-4 w-4" /> Drafter
      </Link>
      {children}
      <p className="mt-12 text-xs text-slate-600">
        Emisphere Esport n'est pas affilié à Riot Games. League of Legends et les visuels des champions sont la propriété de Riot Games, Inc.
      </p>
    </div>
  );
}

function Title({ d, badge }) {
  return (
    <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
      <h1 className="text-xl font-extrabold uppercase tracking-tight sm:text-3xl">
        <span className="text-sky-400">{d.names.blue}</span> <span className="text-slate-500">vs</span> <span className="text-red-500">{d.names.red}</span>
      </h1>
      {badge}
    </div>
  );
}

function SharePanel({ id, tokens }) {
  const base = `${window.location.origin}/drafter/${id}`;
  const links = [
    { key: "blue", label: "Capitaine bleu", url: `${base}#t=${tokens.blue}`, cls: SIDE_STYLE.blue.text },
    { key: "red", label: "Capitaine rouge", url: `${base}#t=${tokens.red}`, cls: SIDE_STYLE.red.text },
    { key: "spectator", label: "Spectateurs", url: base, cls: "text-slate-300" },
  ];
  const copy = (url) => navigator.clipboard.writeText(url).then(() => toast.success("Lien copié"), () => toast.error("Copie impossible"));
  return (
    <details className="card-tactical mt-4 p-4" data-testid="drafter-share">
      <summary className="cursor-pointer font-heading text-sm font-bold uppercase text-orange-500">Liens à partager</summary>
      <p className="mt-2 text-sm text-slate-400">Chaque capitaine joue avec son propre lien. Gardez-les privés : ils donnent le contrôle d'une équipe.</p>
      <ul className="mt-3 grid gap-2">
        {links.map((l) => (
          <li key={l.key} className="flex flex-wrap items-center gap-2">
            <span className={`w-36 shrink-0 text-sm font-bold ${l.cls}`}>{l.label}</span>
            <code className="min-w-0 flex-1 truncate rounded-lg bg-white/[0.04] px-3 py-1.5 text-xs text-slate-400">{l.url}</code>
            <button onClick={() => copy(l.url)} className={SECONDARY_BTN} data-testid={`drafter-copy-${l.key}`}><Copy className="h-4 w-4" /> Copier</button>
          </li>
        ))}
      </ul>
    </details>
  );
}

function OnlineRoom({ id, champions, version, roles }) {
  const { hash } = useLocation();
  const token = new URLSearchParams(hash.slice(1)).get("t") || "";
  const entry = findDraft(id);
  const [d, setD] = useState(null);
  const [offset, setOffset] = useState(0);
  const [status, setStatus] = useState({ ok: true, missing: false });
  const queue = useRef(Promise.resolve());

  const receive = useCallback((data) => {
    setOffset(data.now - Date.now() / 1000);
    setD(data);
    setStatus({ ok: true, missing: false });
  }, []);

  useEffect(() => {
    let alive = true;
    const poll = () => api.get(`/drafts/${id}`, { params: { token } })
      .then((r) => alive && receive(r.data))
      .catch((e) => alive && setStatus({ ok: false, missing: e?.response?.status === 404 }));
    poll();
    const t = setInterval(poll, POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [id, token, receive]);

  // Les actions partent dans l'ordre (un double-clic envoie « sélectionner » puis « valider »).
  const onAction = (_side, action) => {
    queue.current = queue.current
      .then(() => api.post(`/drafts/${id}/action`, { token, ...action }))
      .then((r) => receive(r.data))
      .catch((e) => toast.error(errMsg(e)));
  };

  if (status.missing) return <p className="mt-8 text-slate-400">Cette draft n'existe pas ou le lien est incomplet.</p>;
  if (!d) return <p className="mt-8 text-slate-400">{status.ok ? "Chargement de la draft…" : "Connexion impossible. Nouvelle tentative…"}</p>;

  const badge = (
    <span className="flex items-center gap-2 text-sm text-slate-400">
      {status.ok ? <Wifi className="h-4 w-4 text-green-400" /> : <WifiOff className="h-4 w-4 text-red-400" />}
      {d.you ? <>Capitaine <span className={SIDE_STYLE[d.you].text}>{d.names[d.you]}</span></> : <><Eye className="h-4 w-4" /> Spectateur</>}
    </span>
  );
  return (
    <>
      <Title d={d} badge={badge} />
      {entry?.tokens && <SharePanel id={id} tokens={entry.tokens} />}
      <div className="mt-6">
        <DraftBoard d={d} controls={d.you ? [d.you] : []} champions={champions} version={version} roles={roles} clockOffset={offset} onAction={onAction} />
      </div>
    </>
  );
}

function LocalRoom({ id, champions, version, roles }) {
  const key = `drafter:local:${id}`;
  const [d, setD] = useState(() => load(key, null));
  const ids = champions.map((c) => c.id);

  useEffect(() => { if (d) save(key, d); }, [key, d]);
  useEffect(() => {
    if (!d?.deadline || !ids.length) return undefined;
    const t = setInterval(() => setD((prev) => expire(prev, Date.now() / 1000, ids)), 250);
    return () => clearInterval(t);
    // ids est recalculé à chaque rendu ; seul l'arrivée des champions compte.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d?.deadline, ids.length]);

  if (!d) return <p className="mt-8 text-slate-400">Cette draft locale n'est pas enregistrée sur cet appareil.</p>;

  const onAction = (side, action) => setD((prev) => {
    try {
      return applyAction(prev, side, action, Date.now() / 1000, ids);
    } catch (e) {
      toast.error(e.message);
      return prev;
    }
  });
  const toolbar = (
    <>
      <button onClick={() => setD(undo(d, Date.now() / 1000))} disabled={!d.choices.length} data-testid="drafter-undo" className={SECONDARY_BTN}>
        <Undo2 className="h-4 w-4" /> Annuler
      </button>
      <button onClick={() => setD(newDraft({ ...d, blue: d.previous_games[0]?.names.blue || d.names.blue, red: d.previous_games[0]?.names.red || d.names.red }))}
        data-testid="drafter-reset" className={SECONDARY_BTN}>
        <RotateCcw className="h-4 w-4" /> Recommencer
      </button>
    </>
  );
  const badge = <span className="flex items-center gap-2 text-sm text-slate-400"><HardDrive className="h-4 w-4" /> Local, sur cet appareil</span>;
  return (
    <>
      <Title d={d} badge={badge} />
      <div className="mt-6">
        <DraftBoard d={d} controls={["blue", "red"]} champions={champions} version={version} roles={roles} onAction={onAction} toolbar={toolbar} />
      </div>
    </>
  );
}

export default function DraftRoom({ local = false }) {
  const { id } = useParams();
  const { version, champions, error } = useChampions();
  const roles = useRoles();
  return (
    <Shell>
      {error && <p className="mt-8 text-red-400">Impossible de charger les champions. Connectez-vous une première fois pour les garder sur cet appareil.</p>}
      {champions.length > 0 && (local
        ? <LocalRoom id={id} champions={champions} version={version} roles={roles} />
        : <OnlineRoom id={id} champions={champions} version={version} roles={roles} />)}
    </Shell>
  );
}
